import {
  canAlsoCreateRequest,
  findMentionedMembers,
} from 'features/TeamChat/api/teamChat.transform'
import type { TeamMember } from 'features/TeamChat/api/teamChat.types'
import {
  MESSAGE_MAX_LENGTH,
  REQUEST_MAX_LENGTH,
  TEAM_COPY,
} from 'features/TeamChat/teamChat.constants'
import styles from 'features/TeamChat/teamChat.module.scss'

import { MentionInput } from '../MentionInput'
import { SendIcon } from '../TeamIcons'
import { useTeamDrafts } from '../TeamDrafts'
import type { ChatDraft } from '../TeamDrafts'

export type SendResult = { ok: boolean; error?: string }
type Props = {
  members: TeamMember[]
  onSend: (body: string, createRequest: boolean) => Promise<SendResult>
}

const askedHandles = (draft: string, members: TeamMember[]) =>
  findMentionedMembers(draft, members)
    .filter((m) => !m.is_me)
    .map((m) => `@${m.handle}`)
    .join(', ')

// The request title column holds REQUEST_MAX_LENGTH characters, so longer messages stay plain chat.
const canOfferRequest = (draft: string, members: TeamMember[]) =>
  draft.trim().length <= REQUEST_MAX_LENGTH && canAlsoCreateRequest(draft, members)

// A failed send puts the text back unless something new was typed meanwhile.
const restoreFailed = (state: ChatDraft, sent: string, error?: string): ChatDraft => ({
  ...state,
  draft: state.draft.trim() ? state.draft : sent,
  error: error ?? TEAM_COPY.SEND_FAILED,
})

export const ChatComposer = ({ members, onSend }: Props) => {
  const { chat, setChat } = useTeamDrafts()
  const { draft, error, alsoRequest } = chat
  const offerRequest = canOfferRequest(draft, members)

  // The box clears at once; the message list shows the bubble while the server confirms it.
  const submit = () => {
    const body = draft.trim()
    if (!body) return
    setChat((s) => ({ ...s, draft: '', error: '', alsoRequest: true }))
    void onSend(body, offerRequest && alsoRequest)
      .catch((): SendResult => ({ ok: false }))
      .then((result) => {
        if (!result.ok) setChat((s) => restoreFailed(s, body, result.error))
      })
  }

  return (
    <>
      {offerRequest && (
        <label className={`${styles.also} ${styles.slideUp}`}>
          <input
            type='checkbox'
            className={styles.checkbox}
            checked={alsoRequest}
            onChange={(e) => setChat((s) => ({ ...s, alsoRequest: e.target.checked }))}
          />
          <span>{TEAM_COPY.ALSO_REQUEST(askedHandles(draft, members))}</span>
        </label>
      )}
      <div className={styles.footer} data-mention-anchor>
        <div className={`${styles.row} ${styles.composer}`}>
          <div className={styles.grow}>
            <MentionInput
              id='team-chat-message'
              ariaLabel={TEAM_COPY.MESSAGE_LABEL}
              value={draft}
              members={members}
              maxLength={MESSAGE_MAX_LENGTH}
              placeholder={TEAM_COPY.MESSAGE_PLACEHOLDER}
              onChange={(v) => setChat((s) => ({ ...s, draft: v, error: '' }))}
              onSubmit={submit}
            />
          </div>
          <button
            type='button'
            className={styles.sendButton}
            aria-label={TEAM_COPY.SEND}
            disabled={!draft.trim()}
            onClick={submit}>
            <SendIcon />
          </button>
        </div>
        <div role='alert' className={styles.error}>
          {error}
        </div>
      </div>
    </>
  )
}
